import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { api } from '../utils/api'
import { Printer } from 'lucide-react'

interface WorkOrder {
  workOrderNumber: number; title: string; customerName: string; location: string | null
  description: string | null; scheduledDate: string | null; createdAt: string
  asset: { assetCode: string | null; name: string; serialNumber: string | null } | null
  company: { name: string; logo: string | null } | null
  items: { description: string; isChecked: boolean; note: string | null }[]
  signatures: { role: 'STAFF' | 'CUSTOMER'; signatureUrl: string; signedByName: string | null; signedAt: string }[]
}

export default function WorkOrderPrintPage() {
  const { id } = useParams()

  const { data: wo, isLoading } = useQuery<WorkOrder>({
    queryKey: ['workorder-print', id],
    queryFn: () => api.get(`/workorders/${id}`).then(r => r.data),
    enabled: !!id,
  })

  const fmt = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '.......... / .......... / ..........'
  const staffSignature = wo?.signatures.find(s => s.role === 'STAFF') || null
  const customerSignature = wo?.signatures.find(s => s.role === 'CUSTOMER') || null

  if (isLoading) return <div className="flex items-center justify-center h-screen text-gray-400">กำลังโหลด...</div>
  if (!wo)        return <div className="flex items-center justify-center h-screen text-gray-400">ไม่พบใบงาน</div>

  return (
    <>
      <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2">
        <button onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium shadow-lg hover:bg-blue-700">
          <Printer size={16}/> พิมพ์ / Export PDF
        </button>
        <button onClick={() => window.close()}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm hover:bg-gray-200">
          ปิด
        </button>
      </div>

      <div className="min-h-screen bg-gray-100 print:bg-white py-8 print:py-0">
        <div className="mx-auto bg-white shadow-lg print:shadow-none flex flex-col"
          style={{ width: '210mm', minHeight: '297mm', padding: '15mm 15mm 10mm 15mm' }}>

          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {wo.company?.logo ? (
                <img src={wo.company.logo} alt="logo" className="h-14 max-w-[120px] object-contain"/>
              ) : (
                <div className="w-14 h-14 rounded-full border-2 border-gray-800 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-800">{wo.company?.name?.slice(0, 3).toUpperCase() || 'CO'}</span>
                </div>
              )}
            </div>
            <h1 className="text-xl font-bold text-center flex-1">ใบงาน (Work Order)</h1>
            <div className="w-14 text-right text-xs text-gray-500">#{wo.workOrderNumber}</div>
          </div>

          {/* Info table */}
          <table className="w-full border-collapse border border-gray-800 text-xs mb-3">
            <tbody>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 w-28">หัวข้องาน</td>
                <td className="border border-gray-800 px-2 py-1" colSpan={3}>{wo.title}</td>
              </tr>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1">ลูกค้า / หน่วยงาน</td>
                <td className="border border-gray-800 px-2 py-1">{wo.customerName}</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 w-24">สถานที่</td>
                <td className="border border-gray-800 px-2 py-1">{wo.location || ''}</td>
              </tr>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1">วันที่นัดหมาย</td>
                <td className="border border-gray-800 px-2 py-1">{fmt(wo.scheduledDate)}</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1">Asset</td>
                <td className="border border-gray-800 px-2 py-1">
                  {wo.asset ? `${wo.asset.assetCode || ''} ${wo.asset.name}`.trim() : ''}
                </td>
              </tr>
              {wo.description && (
                <tr>
                  <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1">รายละเอียดงาน</td>
                  <td className="border border-gray-800 px-2 py-1" colSpan={3}><div className="kb-content" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: wo.description }}/></td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Checklist */}
          <table className="w-full border-collapse border border-gray-800 text-xs mb-4">
            <thead>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center w-10">✓</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center w-8">ลำดับ</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center">รายการงาน</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center w-32">หมายเหตุ</td>
              </tr>
            </thead>
            <tbody>
              {wo.items.map((item, idx) => (
                <tr key={idx}>
                  <td className="border border-gray-800 px-2 py-2 text-center">
                    <span className="inline-block w-4 h-4 border-2 border-gray-800" style={{ background: item.isChecked ? '#1e293b' : 'transparent' }}/>
                  </td>
                  <td className="border border-gray-800 px-2 py-2 text-center text-gray-600">{idx + 1}</td>
                  <td className="border border-gray-800 px-2 py-2">{item.description}</td>
                  <td className="border border-gray-800 px-2 py-2">{item.note || ''}</td>
                </tr>
              ))}
              {wo.items.length === 0 && (
                <tr><td colSpan={4} className="border border-gray-800 px-2 py-4 text-center text-gray-400">ไม่มีรายการ</td></tr>
              )}
            </tbody>
          </table>

          {/* Signature */}
          <div className="grid grid-cols-2 gap-16 text-xs mt-auto pt-8">
            <div>
              <div className="h-16 mb-1 flex items-end justify-center">
                {staffSignature && <img src={staffSignature.signatureUrl} alt="signature" className="max-h-16 object-contain"/>}
              </div>
              <p className="mb-1 flex items-baseline gap-1">
                <span className="flex-shrink-0">ผู้ปฏิบัติงาน</span>
                <span className="flex-1 border-b border-gray-800 h-3"/>
              </p>
              <p className="mt-3">วันที่ {staffSignature ? fmt(staffSignature.signedAt) : '.......... / .......... / ..........'}</p>
            </div>
            <div>
              <div className="h-16 mb-1 flex items-end justify-center">
                {customerSignature && <img src={customerSignature.signatureUrl} alt="signature" className="max-h-16 object-contain"/>}
              </div>
              <p className="mb-1 flex items-baseline gap-1">
                <span className="flex-shrink-0">ผู้รับมอบงาน{customerSignature ? `: ${customerSignature.signedByName || ''}` : ' (ลูกค้า)'}</span>
                <span className="flex-1 border-b border-gray-800 h-3"/>
              </p>
              <p className="mt-3">วันที่ {customerSignature ? fmt(customerSignature.signedAt) : '.......... / .......... / ..........'}</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { margin: 0; }
        }
      `}</style>
    </>
  )
}
